import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import api from "@/lib/api";
import BalbisLogo from "@/components/BalbisLogo";
import { LogOut, ClipboardList, Home, Building2, FileText, Bell, MessageSquare, Menu, X, User } from "lucide-react";

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState({ count: 0, threads: [] });
  const [showBell, setShowBell] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bellRef = useRef(null);

  const fetchUnread = () =>
    api.get("/portal/notifications/unread").then((r) => setUnread(r.data)).catch(() => {});

  useEffect(() => { fetchUnread(); }, []);

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.sender_type === "agency") fetchUnread();
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    const handler = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setShowBell(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Auto-clear portal bell when Messages page is visited
  useEffect(() => {
    if (location.pathname === "/portal/messages") {
      setUnread({ count: 0, threads: [] });
    }
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to || (to !== "/portal" && location.pathname.startsWith(to));

  const navItems = [
    { to: "/portal", icon: Home, label: "Home", testId: "portal-nav-home" },
    { to: "/portal/requests", icon: ClipboardList, label: "Tasks", testId: "portal-nav-requests" },
    { to: "/portal/messages", icon: MessageSquare, label: "Messages", testId: "portal-nav-messages", badge: unread.count },
    { to: "/portal/invoices", icon: FileText, label: "Invoices", testId: "portal-nav-invoices" },
    ...(user?.role === "client_lead" ? [{ to: "/portal/organisation", icon: Building2, label: "Org", testId: "portal-nav-organisation" }] : []),
  ];

  return (
    <div className="min-h-screen pb-16 md:pb-0" style={{ background: "#FBF9F6" }}>
      {/* Header */}
      <header className="border-b sticky top-0 z-30" style={{ background: "#FFFFFF", borderColor: "#E7E5E4" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <BalbisLogo style={{ height: "18px", width: "auto", color: "#292524" }} />
            <span className="text-xs tracking-widest uppercase font-medium border-l pl-2.5 hidden sm:block" style={{ color: "#A8A29E", borderColor: "#E7E5E4" }}>
              Service Hub
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, icon: Icon, label, testId, badge }) => {
              const active = isActive(to);
              return (
                <Link key={to} to={to} data-testid={testId}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors hover:bg-stone-100 relative"
                  style={{ color: active ? "#292524" : "#57534E", fontWeight: active ? 500 : 400 }}
                >
                  <Icon size={14} strokeWidth={1.5} />
                  <span>{label === "Org" ? "Organisation" : label}</span>
                  {badge > 0 && (
                    <span className="w-4 h-4 rounded-full text-white flex items-center justify-center font-bold" style={{ background: "#D97706", fontSize: "10px" }}>{badge}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right: bell + user + mobile menu btn */}
          <div className="flex items-center gap-1">
            {/* Notification bell */}
            <div className="relative" ref={bellRef}>
              <button data-testid="portal-notification-bell" onClick={() => setShowBell((p) => !p)}
                className="relative p-2 rounded-lg hover:bg-stone-100 transition-colors" style={{ color: "#78716C" }}>
                <Bell size={16} strokeWidth={1.5} />
                {unread.count > 0 && (
                  <span data-testid="portal-notification-badge"
                    className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white flex items-center justify-center font-bold"
                    style={{ background: "#DC2626", fontSize: "10px" }}>
                    {unread.count > 9 ? "9+" : unread.count}
                  </span>
                )}
              </button>
              {showBell && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl border shadow-lg z-50 overflow-hidden"
                  style={{ borderColor: "#E7E5E4" }} data-testid="portal-notification-dropdown">
                  <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E7E5E4" }}>
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>New Replies</p>
                    {unread.count > 0 && (
                      <button data-testid="portal-mark-all-read"
                        onClick={async () => { await api.post("/portal/notifications/mark-all-read"); setUnread({ count: 0, threads: [] }); }}
                        className="text-xs font-medium" style={{ color: "#D97706" }}>Mark all read</button>
                    )}
                  </div>
                  {(unread.threads || []).length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm" style={{ color: "#A8A29E" }}>All caught up!</div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: "#F5F3EF" }}>
                      {(unread.threads || []).map((t) => (
                        <button key={t.id} data-testid={`portal-notif-item-${t.id}`}
                          onClick={() => { navigate("/portal/messages"); setShowBell(false); }}
                          className="w-full text-left px-4 py-3 hover:bg-stone-50 transition-colors">
                          <p className="text-sm font-medium truncate" style={{ color: "#292524" }}>{t.title}</p>
                          <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>New reply from Balbis</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Desktop: user + logout */}
            <button data-testid="portal-user-profile-link" onClick={() => navigate("/portal/profile")}
              className="hidden md:flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-100 transition-colors">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold overflow-hidden flex-shrink-0"
                style={{ background: user?.avatar_image ? "transparent" : (user?.avatar_color || "#D97706") }}>
                {user?.avatar_image ? <img src={user.avatar_image} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
              </div>
              <span className="text-sm" style={{ color: "#292524" }}>{user?.name}</span>
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="portal-logout-button"
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors hover:bg-red-50"
              style={{ color: "#DC2626" }}>
              <LogOut size={14} strokeWidth={1.5} /><span>Logout</span>
            </button>

            {/* Mobile hamburger */}
            <button className="md:hidden p-2 rounded-lg hover:bg-stone-100" style={{ color: "#78716C" }}
              onClick={() => setMobileMenuOpen(true)}>
              <Menu size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-down menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 w-64 h-full bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "#E7E5E4" }}>
              <BalbisLogo style={{ height: "18px", width: "auto", color: "#292524" }} />
              <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-stone-100" style={{ color: "#78716C" }}>
                <X size={16} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {navItems.map(({ to, icon: Icon, label, testId, badge }) => {
                const active = isActive(to);
                return (
                  <Link key={to} to={to} data-testid={testId}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors"
                    style={{ background: active ? "#F5F3EF" : "transparent", color: active ? "#292524" : "#57534E", fontWeight: active ? 500 : 400 }}>
                    <Icon size={16} strokeWidth={1.5} />
                    <span>{label === "Org" ? "Organisation" : label}</span>
                    {badge > 0 && <span className="ml-auto w-5 h-5 rounded-full text-white flex items-center justify-center font-bold" style={{ background: "#D97706", fontSize: "10px" }}>{badge}</span>}
                  </Link>
                );
              })}
            </nav>
            <div className="px-3 py-4 border-t space-y-1" style={{ borderColor: "#E7E5E4" }}>
              <button onClick={() => navigate("/portal/profile")}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm hover:bg-stone-50" style={{ color: "#57534E" }}>
                <User size={16} strokeWidth={1.5} />{user?.name || "Profile"}
              </button>
              <button onClick={() => { logout(); navigate("/login"); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm hover:bg-red-50" style={{ color: "#DC2626" }}>
                <LogOut size={16} strokeWidth={1.5} />Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 border-t md:hidden" style={{ background: "#FFFFFF", borderColor: "#E7E5E4" }}>
        <div className="flex">
          {navItems.slice(0, 5).map(({ to, icon: Icon, label, testId, badge }) => {
            const active = isActive(to);
            return (
              <Link key={to} to={to} data-testid={`mobile-${testId}`}
                className="flex-1 flex flex-col items-center justify-center py-2.5 gap-1 transition-colors"
                style={{ color: active ? "#292524" : "#A8A29E" }}>
                <div className="relative">
                  <Icon size={18} strokeWidth={active ? 2 : 1.5} />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-white flex items-center justify-center font-bold" style={{ background: "#DC2626", fontSize: "9px" }}>{badge > 9 ? "9+" : badge}</span>
                  )}
                </div>
                <span className="text-xs" style={{ fontSize: "10px", fontWeight: active ? 600 : 400 }}>{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8">{children}</main>
    </div>
  );
}
